import PropTypes from "prop-types";
import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://www.palmcaresinaihospital.co.ke";
const SITE_NAME = "PalmCare Sinai Hospital";
const DEFAULT_DESCRIPTION =
  "PalmCare Sinai Hospital is a Level 4 private hospital in Kimumu, Eldoret, Kenya providing 24/7 outpatient, inpatient, specialist clinics, maternity, laboratory, radiology, theatre and pharmacy services. NHIF accredited.";

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESCRIPTION,
};

const hospitalSchema = {
  "@context": "https://schema.org",
  "@type": "Hospital",
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/img/logo.png`,
  image: `${SITE_URL}/img/slider/DSS_6740.jpg`,
  description: DEFAULT_DESCRIPTION,
  telephone: "+254713000055",
  email: "info@palmcaresinaihospital.co.ke",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Kimumu, Eldoret-Iten Road, Moiben",
    addressLocality: "Eldoret",
    addressRegion: "Uasin Gishu County",
    addressCountry: "KE",
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ],
    opens: "00:00",
    closes: "23:59",
  },
  availableService: [
    {
      "@type": "MedicalProcedure",
      name: "General Outpatient Services",
    },
    {
      "@type": "MedicalProcedure",
      name: "Specialist Clinics",
    },
    {
      "@type": "MedicalProcedure",
      name: "Maternity and Newborn Unit",
    },
    {
      "@type": "MedicalProcedure",
      name: "Radiology Services",
    },
    {
      "@type": "MedicalProcedure",
      name: "Laboratory Services",
    },
    {
      "@type": "MedicalProcedure",
      name: "Theatres",
    },
  ],
};

const SEO = ({ title, description, keywords, image }) => {
  const { pathname } = useLocation();
  const canonical = `${SITE_URL}${pathname}`;
  const metaDescription = description || DEFAULT_DESCRIPTION;
  const metaImage = `${SITE_URL}${image || "/img/slider/DSS_6740.jpg"}`;
  const isHome = pathname === "/";

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content="index, follow" />
      <meta name="author" content={SITE_NAME} />
      <link rel="canonical" href={canonical} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:locale" content="en_KE" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />

      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      {isHome && (
        <script type="application/ld+json">
          {JSON.stringify(hospitalSchema)}
        </script>
      )}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  keywords: PropTypes.string,
  image: PropTypes.string,
};

export default SEO;
